import moment from 'moment';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import type { DayAvailability, OptionsPanelConfig } from 'voyager';
import { Price } from 'shared-types/Common';
import { TimeSlotType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues } from '../SpaPackageForm.schema';

type GuestConfigGroups = SpaPackageFormValues['guests'][number]['configurationGroups'];

export function mapTimeslotsToDayAvailability(
  timeslots: Record<string, TimeSlotType[]> | undefined,
  startDate: string
): DayAvailability[] {
  if (!timeslots) return [];
  const days: DayAvailability[] = [];
  for (let i = 0; i < 7; i++) {
    const date = moment(startDate).add(i, 'days').format('YYYY-MM-DD');
    const slots = timeslots[date];
    days.push({
      date,
      available: !!slots && slots.length > 0,
    });
  }
  return days;
}

export function formatTime(time: string): string {
  return time.replace(/^(\d{2}:\d{2}):\d{2}$/, '$1');
}

export function mapConfigGroupToOptionsPanel(
  group: ConfigurationGroupType,
  guestSelections: GuestConfigGroups
): OptionsPanelConfig | null {
  if (group.isExtras) return null;
  if (!group.isRequired) return null;

  const groupKey = `group_${group.id}`;
  const selections = guestSelections?.[groupKey] as
    | { optionId: number; isSelected: boolean; timeslot: { startTime: string } | null }[]
    | undefined;

  const mapOptionBase = (opt: ConfigurationGroupType['options'][number]) => ({
    id: String(opt.id),
    label: opt.name || '',
    imageUrl: opt.image?.url,
    imageAlt: opt.name,
    timeslots: (opt.timeslots || []).map((ts) => ({
      value: ts.startTime,
      label: formatTime(ts.startTime),
    })),
  });

  if (group.supportsSelections) {
    const min = group.minimumSelections ?? 1;
    const max = group.maximumSelections ?? 1;

    if (max > 1 || min > 1) {
      return {
        type: 'multi' as const,
        title: group.name,
        options: group.options.map((opt, idx) => {
          const selection = selections?.[idx];
          const isSelected = !!selection?.isSelected;
          return {
            ...mapOptionBase(opt),
            isSelected,
            selectedTimeslot: isSelected ? selection?.timeslot?.startTime : undefined,
          };
        }),
        maximumSelections: max,
      };
    }

    const selectedEntry = selections?.find((s) => s?.isSelected);

    return {
      type: 'single' as const,
      title: group.name,
      options: group.options.map(mapOptionBase),
      selectedOptionId: selectedEntry ? String(selectedEntry.optionId) : undefined,
      selectedTimeslot: selectedEntry?.timeslot?.startTime,
    };
  }

  return {
    type: 'all' as const,
    title: group.name,
    options: group.options.map((opt, idx) => ({
      ...mapOptionBase(opt),
      selectedTimeslot: selections?.[idx]?.timeslot?.startTime,
    })),
  };
}

export function calculateTotalPrice(entityPrice: Price, count: number, extras: number): Price {
  return {
    ...entityPrice,
    amount: entityPrice.amount * count + extras,
  };
}
